<template>
  <div
    v-if="computedProducts.length"
    :class="$style.productSet"
    data-product-set-v="3.1.0"
  >
    <BaseHeading size="h3" :class="$style.header">
      {{ data?.fields?.heading }}
    </BaseHeading>
    <ProductSelector
      v-if="computedProducts.length > 1"
      :products="computedProducts"
      :selected-product="activeProduct"
      @set-active-product="setActiveProduct"
    />
    <ProductListing
      v-if="activeProduct"
      :key="activeVariant?.sys?.id || activeProduct?.sys?.id"
      :data="activeVariant || activeProduct"
      :is-event-page="isEventPage"
      :variants="computedVariants"
      :selected-variant="activeVariant"
      @set-active-variant="setActiveVariant"
    />
  </div>
</template>

<script setup lang="ts">
import type {
  TypeProductHighlightWithoutUnresolvableLinksResponse,
  TypeProductSetWithoutUnresolvableLinksResponse,
  TypeProductWithoutUnresolvableLinksResponse,
} from '~/types/contentful'

const { $visibility, $userAccessCheck } = useNuxtApp()
const { highestUserLevel } = storeToRefs(useUserStore())
const route = useRoute()
const router = useRouter()

const props = defineProps<{
  data:
    | TypeProductSetWithoutUnresolvableLinksResponse
    | TypeProductHighlightWithoutUnresolvableLinksResponse
    | undefined
}>()

// Data
const activeProduct = ref<TypeProductWithoutUnresolvableLinksResponse>()
const activeVariant = ref<TypeProductWithoutUnresolvableLinksResponse>()

// Computed
const computedProducts = computed(() => {
  return (
    props.data?.fields?.products?.filter((p) => {
      const isVisible = $visibility(p?.fields?.visibility)
      const hasAccess = $userAccessCheck(
        p?.fields?.accessLevel,
        highestUserLevel.value,
      )
      return isVisible && hasAccess
    }) || []
  )
})

const computedVariants = computed(() => {
  return (
    activeProduct.value?.fields?.variants?.filter((p) => {
      const isVisible = $visibility(p?.fields?.visibility)
      const hasAccess = $userAccessCheck(
        p?.fields?.accessLevel,
        highestUserLevel.value,
      )
      return isVisible && hasAccess
    }) || []
  )
})

watchEffect(() => {
  if (!activeProduct.value && computedProducts.value.length > 0) {
    activeProduct.value = computedProducts.value[0]
  }
})

const isEventPage = computed(() => route?.path?.includes?.('/event/'))

// Methods
const getVisibleVariants = (
  product: TypeProductWithoutUnresolvableLinksResponse | undefined,
) => {
  return (
    product?.fields?.variants?.filter((p) => {
      const isVisible = $visibility(p?.fields?.visibility)
      const hasAccess = $userAccessCheck(
        p?.fields?.accessLevel,
        highestUserLevel.value,
      )
      return isVisible && hasAccess
    }) || []
  )
}

const resolveProductQuerySelection = (id: string) => {
  for (const product of computedProducts.value) {
    const variant = getVisibleVariants(product).find(
      (variant) => variant?.sys?.id === id,
    )

    if (variant) {
      return { product, variant }
    }
  }

  const product = computedProducts.value?.find?.(
    (product) => product?.sys?.id === id,
  )

  return { product, variant: undefined }
}

const setActiveProduct = (id: string) => {
  activeProduct.value =
    computedProducts.value?.find?.((product) => product?.sys?.id === id) ||
    computedProducts.value?.[0]
  activeVariant.value = computedVariants.value?.[0]
  if (id) {
    router.push({ query: { ...route.query, product: id } })
  }
}

const setInitialProduct = () => {
  const {
    query: { product: productQuery = '' },
  } = route

  const productQueryAsString = Array.isArray(productQuery)
    ? productQuery.join()
    : productQuery

  if (!productQueryAsString) return

  const { product, variant } =
    resolveProductQuerySelection(productQueryAsString)

  activeProduct.value = product || computedProducts.value?.[0]
  activeVariant.value = variant || computedVariants.value?.[0]
}

const setActiveVariant = (id: string) => {
  activeVariant.value =
    computedVariants.value?.find?.((product) => product?.sys?.id === id) ||
    computedVariants.value?.[0]

  if (id) {
    router.push({ query: { ...route.query, product: id } })
  }
}

onMounted(() => {
  setInitialProduct()
})
</script>

<style lang="scss" module>
.productSet {
  overflow: hidden;
  padding: 50px 0;
}

.header {
  margin-bottom: 35px;
  text-align: center;
}
</style>
